import { useSyncExternalStore } from 'react';
import { t } from '@/shared/i18n';
import { readStorage, writeStorage } from '@/shared/lib/storage';

/**
 * «Доступно офлайн» — как в Dropbox и 1Password: человек сам отмечает беседы, которые обязаны быть с ним без сети,
 * и выбирает, за какой период держать историю. Выбор свой у каждого устройства (на ноутбуке можно держать больше,
 * чем на телефоне) и у каждого человека: ключ хранилища включает его номер.
 */
export const OFFLINE_PERIODS = ['7d', '30d', '90d', 'all'] as const;
export type OfflinePeriod = (typeof OFFLINE_PERIODS)[number];

export interface OfflinePin {
  readonly period: OfflinePeriod;
  /** Когда отметили: новые — вверху списка в настройках. */
  readonly at: number;
}

/** Номер пространства → номер беседы → отметка. */
type AllPins = Readonly<Record<string, Readonly<Record<string, OfflinePin>>>>;

export const PINS_EVENT = 'chat-offline-pins';
const DEFAULT_PERIOD_KEY = 'chat.offline.period';
const DAY = 86_400_000;

const pinsKey = (uid: string) => `chat.offline.pins.${uid}`;

/** Поля разобранного JSON без приведения типов. */
export function entriesOf(x: object): [string, unknown][] {
  return Object.keys(x).map((key): [string, unknown] => {
    const value: unknown = Reflect.get(x, key);
    return [key, value];
  });
}

function isPeriod(value: unknown): value is OfflinePeriod {
  return OFFLINE_PERIODS.some((p) => p === value);
}

function parse(raw: string | null): AllPins {
  if (raw === null) return {};
  try {
    const data: unknown = JSON.parse(raw);
    if (typeof data !== 'object' || data === null) return {};
    const out: Record<string, Record<string, OfflinePin>> = {};
    for (const [wid, list] of entriesOf(data)) {
      if (typeof list !== 'object' || list === null) continue;
      const pins: Record<string, OfflinePin> = {};
      for (const [cid, pin] of entriesOf(list)) {
        const period: unknown = typeof pin === 'object' && pin !== null ? Reflect.get(pin, 'period') : null;
        const at: unknown = typeof pin === 'object' && pin !== null ? Reflect.get(pin, 'at') : null;
        if (isPeriod(period)) pins[cid] = { period, at: typeof at === 'number' ? at : 0 };
      }
      out[wid] = pins;
    }
    return out;
  } catch {
    return {};
  }
}

/** Разбор строки хранилища — один на каждую строку: useSyncExternalStore требует один и тот же объект, пока данные не менялись. */
let cache: { raw: string | null; pins: AllPins } = { raw: null, pins: {} };
function allPins(uid: string): AllPins {
  const raw = readStorage(pinsKey(uid));
  if (raw !== cache.raw) cache = { raw, pins: parse(raw) };
  return cache.pins;
}

const EMPTY: Readonly<Record<string, OfflinePin>> = {};

export function readPins(uid: string, wid: string): Readonly<Record<string, OfflinePin>> {
  return allPins(uid)[wid] ?? EMPTY;
}

/** Отметить беседу (period) или снять отметку (null). */
export function setPin(uid: string, wid: string, cid: string, period: OfflinePeriod | null): void {
  const all = allPins(uid);
  const before = all[wid] ?? {};
  const list = Object.fromEntries(Object.entries(before).filter(([id]) => id !== cid));
  if (period !== null) list[cid] = { period, at: before[cid]?.at ?? Date.now() };
  writeStorage(pinsKey(uid), JSON.stringify({ ...all, [wid]: list }));
  window.dispatchEvent(new CustomEvent(PINS_EVENT, { detail: { wid, cid, period } }));
}

/** Стереть все отметки человека на этом устройстве (выход, смена учётной записи). */
export function clearPins(uid: string): void {
  writeStorage(pinsKey(uid), null);
  window.dispatchEvent(new Event(PINS_EVENT));
}

function subscribe(listener: () => void): () => void {
  window.addEventListener(PINS_EVENT, listener);
  // Другая вкладка поменяла отметки.
  window.addEventListener('storage', listener);
  return () => {
    window.removeEventListener(PINS_EVENT, listener);
    window.removeEventListener('storage', listener);
  };
}

export function useOfflinePins(uid: string, wid: string): Readonly<Record<string, OfflinePin>> {
  const get = () => readPins(uid, wid);
  return useSyncExternalStore(subscribe, get, get);
}

export function readDefaultPeriod(): OfflinePeriod {
  const raw = readStorage(DEFAULT_PERIOD_KEY);
  return isPeriod(raw) ? raw : '30d';
}

export function writeDefaultPeriod(period: OfflinePeriod): void {
  writeStorage(DEFAULT_PERIOD_KEY, period);
  window.dispatchEvent(new Event(PINS_EVENT));
}

export function useDefaultPeriod(): OfflinePeriod {
  return useSyncExternalStore(subscribe, readDefaultPeriod, readDefaultPeriod);
}

/** С какого момента держим историю; null — всю. */
export function periodStart(period: OfflinePeriod, now: number): number | null {
  if (period === '7d') return now - 7 * DAY;
  if (period === '30d') return now - 30 * DAY;
  if (period === '90d') return now - 90 * DAY;
  return null;
}

export function periodLabel(period: OfflinePeriod): string {
  if (period === '7d') return t('7 дней');
  if (period === '30d') return t('30 дней');
  if (period === '90d') return t('3 месяца');
  return t('Вся история');
}

// ——— Состояние скачивания истории (знак у строки). Здесь, а не отдельным файлом: этот код и так в первой загрузке,
// а отдельный общий кусок попал бы в списки предзагрузки многих разделов. ———

export type HistoryState = 'loading' | 'error';

export const STATE_EVENT = 'chat-offline-history';
/** Копию стёрли целиком: отмеченное скачивается заново (OfflineKeeper). */
export const HISTORY_CLEARED_EVENT = 'chat-offline-cleared';

export const recordKey = (wid: string, cid: string) => `${wid}:${cid}`;

// ——— Состояние загрузки — для значка в списке ———

const states = new Map<string, HistoryState>();

export function setState(cid: string, state: HistoryState | null): void {
  if (state === null) states.delete(cid);
  else states.set(cid, state);
  window.dispatchEvent(new Event(STATE_EVENT));
}

export function subscribeState(listener: () => void): () => void {
  window.addEventListener(STATE_EVENT, listener);
  return () => {
    window.removeEventListener(STATE_EVENT, listener);
  };
}

export function useHistoryState(cid: string): HistoryState | null {
  const get = () => states.get(cid) ?? null;
  return useSyncExternalStore(subscribeState, get, get);
}

/** Забыть состояние всех бесед (копию стёрли). */
export function clearStates(): void {
  states.clear();
  window.dispatchEvent(new Event(STATE_EVENT));
}
